import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import { fcDelete } from '@seeqdev/icons/seeq';
import Icon from '../Icon';
import { colors } from '../theme/colors.stylex';
import { controlMetrics } from '../theme/controls.stylex';
import type { AlertProps } from './Alert.types';

const styles = stylex.create({
  base: {
    boxSizing: 'border-box',
    marginLeft: 'auto',
    marginRight: 'auto',
    lineHeight: 1.5,
    outlineStyle: 'none',
    paddingTop: '0.5rem',
    paddingBottom: '0.5rem',
    paddingLeft: '0.75rem',
    paddingRight: '0.75rem',
    borderRadius: '10px',
    width: '100%',
    position: 'relative',
    borderStyle: 'solid',
    borderWidth: controlMetrics.borderWidth,
    fontSize: '0.875rem',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  close: { alignSelf: 'flex-end', cursor: 'pointer', position: 'absolute', right: '0.625rem', top: '0.625rem' },
  closeText: { color: colors.text },
});
const variants = stylex.create({
  theme: { backgroundColor: colors.alertBackground, color: colors.bodyText, borderColor: colors.accent },
  formulaError: {
    backgroundColor: colors.formulaErrorBackground,
    color: colors.formulaErrorText,
    borderColor: colors.formulaErrorBorder,
    borderRadius: '0.25rem',
  },
  danger: { backgroundColor: colors.borderInvalid, color: colors.textOnLight, borderColor: colors.borderInvalid },
  warning: { backgroundColor: colors.warningBackground, color: colors.warningText, borderWidth: 0 },
  gray: { backgroundColor: colors.surfaceHover, color: colors.bodyText, borderColor: colors.textMuted },
});

/**
 * Alert.
 */
export const Alert = ({
  children,
  dismissible = true,
  onClose,
  show = true,
  variant,
  testId = 'alert-id',
  id,
  extraClassNames,
  xstyle,
  ...tooltipProps
}: AlertProps) => {
  const styleProps = stylex.props(styles.base, variants[variant], xstyle);

  if (!show) {
    return <div />;
  }
  return (
    <div
      data-testid={testId}
      id={id}
      {...styleProps}
      data-variant={variant}
      className={clsx(styleProps.className, extraClassNames)}>
      {children}
      {dismissible && (
        <Icon
          icon={fcDelete}
          type={variant.match(/danger|warning|formulaError/) ? 'color' : 'theme'}
          color={variant.match(/danger|warning|formulaError/) ? 'inherit' : undefined}
          xstyle={[styles.close, !!variant.match(/danger|warning|gray|formulaError/) && styles.closeText]}
          testId={`${testId}-close-btn`}
          onClick={onClose}
          {...tooltipProps}
        />
      )}
    </div>
  );
};
